import React, { createContext, useContext } from 'react'

import MODAL from '../utils/modals'

import { useServiceScheduleContext, actionTypes } from './serviceScheduleContext'
import { getNames } from 'src/services/professionalService'

export const ProfessionalContext = createContext()

// contexto de estado
export function ProfessionalContextProvider ({ children }) {
  const { state, dispatch, functions: schedulingFunctions } = useServiceScheduleContext()
  const { checkReservedDate } = schedulingFunctions

  // seta as datas disponiveis de acordo com a data selecionada
  const setAvailableDatesByProfessional = (professionalId) => {
    const { availableSlots, scheduleBody, error } = state

    let newState = {
      ...state
    }

    // Inicialização dos arrays de dias disponíveis
    const availableDates = availableSlots[professionalId]
      ? Object.keys(availableSlots[professionalId]).map((day) => parseInt(day))
      : []

    newState = {
      ...newState,
      scheduleBody: {
        ...scheduleBody,
        professional_id: professionalId
      },
      error: {
        ...error,
        professional_id: undefined
      },
      availableDates
    }

    return newState
  }

  // abre modal do profissional
  const handleViewProfessional = async (professionalId, e) => {
    e.stopPropagation()
    const { serviceData, partnersData } = state
    const professionalList = await getNames([professionalId])
    const professional = professionalList[0]
    const servicesList = serviceData.filter((service) =>
      professional.services.includes(service.value)
    )

    const partner = partnersData.find((partner) => partner.id === professional.partner)
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.PROFESSIONAL_PROFILE(professional, servicesList, partner)
      }
    })
  }

  // seleciona o primeiro profissional da lista
  const handleNoPreferenceProfessional = (props) => {
    const { context } = props

    const { professionalData } = state

    context.showLoader()

    const newState = setAvailableDatesByProfessional(professionalData[0]?.id)

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        ...newState,
        noPreference: true
      }
    })

    context.hideLoader()
  }

  // seleciona profissinal
  const handleSelectedProfessional = (professionalId, props) => {
    const { context } = props

    context.showLoader()

    const newState = setAvailableDatesByProfessional(professionalId)

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        ...newState,
        noPreference: false
      }
    })

    context.hideLoader()
  }

  // busca profissionais
  const getProfessionals = async (props) => {
    const { context } = props

    context.showLoader()

    const { availableSlots, formList, havePayment, scheduleBody } = state
    const { selectDay, selectTime } = scheduleBody

    let professionalIds = Object.keys(availableSlots).filter(
      (professionalId) => professionalId !== ''
    )

    // Quando samePetDate é true ou já existe data/hora selecionada, filtra os profissionais
    // disponíveis para a data/hora já selecionada, considerando a duração dos serviços
    if (selectDay && selectTime) {
      const day = parseInt(selectDay.split('-').pop(), 10)

      // Filtra profissionais que estão disponíveis no horário selecionado
      professionalIds = professionalIds.filter((professional) =>
        availableSlots[professional]?.[day]?.includes(selectTime)
      )

      // Filtra profissionais usando checkReservedDate que considera a duração dos serviços
      // para evitar sobreposição de horários em múltiplos agendamentos
      if (formList.length > 0) {
        professionalIds = professionalIds.filter((professional) =>
          checkReservedDate({ professional, selectDay, selectTime })
        )
      }
    }

    const professionalList = await getNames(professionalIds)

    const professionalData = professionalList
      .map(({ id, name, photo, enabledPayment, ratingAverage }) => ({ id, name, photo, enabledPayment: enabledPayment || false, ratingAverage }))
      .filter(({ enabledPayment }) => !formList.length || (havePayment && enabledPayment === havePayment) || !havePayment)
      .filter(({ id }) => {
        // Usa checkReservedDate para verificar sobreposição considerando duração
        if (formList.length > 0 && selectDay && selectTime) {
          return checkReservedDate({ professional: id, selectDay: state.scheduleBody.selectDay, selectTime: state.scheduleBody.selectTime })
        }
        return true
      })

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        professionalData
      }
    })

    context.hideLoader()
  }

  // exporta as funções
  const functions = {
    handleViewProfessional,
    handleNoPreferenceProfessional,
    handleSelectedProfessional,
    getProfessionals
  }

  return (
    <ProfessionalContext.Provider value={{ functions }}>
      {children}
    </ProfessionalContext.Provider>
  )
}

// Hook para acessar o contexto
export function useProfessionalContext () {
  const context = useContext(ProfessionalContext)
  if (!context) {
    throw new Error('useProfessionalContext deve ser usado dentro de um Provider!')
  }

  const { functions } = context
  return { functions }
}
